/**
 * ProductFormModal - Create/Edit modal for products
 * Supports all pricing types: matrix, per_sqft, option_matrix, fixed
 * Phase 6: Full admin catalog management
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { 
  Loader2, 
  Package, 
  DollarSign, 
  Layers, 
  Save,
  X,
  Plus,
  Trash2,
  Image as ImageIcon,
  Upload,
  AlertTriangle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Pricing type options
const PRICING_TYPES = [
  { value: 'fixed', label: 'Fixed Price', description: 'Single price for the product' },
  { value: 'matrix', label: 'Matrix (Variants)', description: 'Different prices for size/color variants' },
  { value: 'per_sqft', label: 'Per Square Foot', description: 'Price calculated by area' },
  { value: 'option_matrix', label: 'Option Matrix', description: 'Complex options with price combinations' }
];

const ProductFormModal = ({ open, onOpenChange, productId = null, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [originalPricingType, setOriginalPricingType] = useState(null);
  
  // Categories for selection
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    description: '',
    category_id: '',
    pricing_type: 'fixed',
    pricing_source: 'manual',
    retail_price: 0,
    cost_price: 0,
    lead_time_days: 7,
    is_active: true,
    taxable: true,
    image_url: '',
    images: [],
    tags: [],
    // Matrix pricing
    variants: [],
    // Per sqft pricing
    sqft_pricing: { rate_per_sqft: 0, min_sqft: 1, min_charge: 0 },
    material_options: [],
    // Option matrix pricing
    options: {},
    price_table: []
  });

  const isEditMode = !!productId;

  // Load categories function
  const loadCategories = async () => {
    setLoadingCategories(true);
    try {
      const res = await fetch(`${API_URL}/api/catalog/categories`);
      if (res.ok) {
        const data = await res.json();
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
    setLoadingCategories(false);
  };

  // Load product function
  const loadProduct = async (pid) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/catalog/products/${pid}`);
      if (res.ok) {
        const data = await res.json();
        const pricingType = data.pricing_type || 'fixed';
        setOriginalPricingType(pricingType);
        setFormData({
          name: data.name || '',
          sku: data.sku || '',
          description: data.description || '',
          category_id: data.category_id || '',
          pricing_type: pricingType,
          pricing_source: data.pricing_source || 'manual',
          retail_price: data.retail_price || data.base_price || 0,
          cost_price: data.cost_price || 0,
          lead_time_days: data.lead_time_days || 7,
          is_active: data.is_active !== false,
          taxable: data.taxable !== false,
          image_url: data.image_url || '',
          images: data.images || [],
          tags: data.tags || [],
          variants: data.variants || [],
          sqft_pricing: data.sqft_pricing || { rate_per_sqft: 0, min_sqft: 1, min_charge: 0 },
          material_options: data.material_options || [],
          options: data.options || {},
          price_table: data.price_table || data.option_pricing?.price_table || []
        });
      }
    } catch (error) {
      console.error('Error loading product:', error);
      toast.error('Failed to load product');
    }
    setLoading(false);
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload a JPG, PNG, WebP, or GIF image.');
      return;
    }
    
    // For new products, we need to save first
    if (!productId) {
      toast.error('Please save the product first, then upload an image.');
      return;
    }
    
    setUploadingImage(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append('file', file);
      
      const res = await fetch(`${API_URL}/api/catalog/products/${productId}/upload-image`, {
        method: 'POST',
        body: formDataObj
      });
      
      if (res.ok) {
        const data = await res.json();
        handleChange('image_url', data.image_url);
        toast.success('Image uploaded successfully');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to upload image');
      }
    } catch (error) {
      toast.error('Error uploading image');
      console.error(error);
    }
    setUploadingImage(false);
  };

  // Load categories on mount
  useEffect(() => {
    loadCategories();
  }, []);

  // Load product data if editing
  useEffect(() => {
    if (open && productId) {
      loadProduct(productId);
    } else if (open && !productId) {
      setOriginalPricingType(null);
      // Reset form for new product
      setFormData({
        name: '',
        sku: '',
        description: '',
        category_id: '',
        pricing_type: 'fixed',
        pricing_source: 'manual',
        retail_price: 0,
        cost_price: 0,
        lead_time_days: 7,
        is_active: true,
        taxable: true,
        image_url: '',
        images: [],
        tags: [],
        variants: [],
        sqft_pricing: { rate_per_sqft: 0, min_sqft: 1, min_charge: 0 },
        material_options: [],
        options: {},
        price_table: []
      });
      setActiveTab('basic');
    }
  }, [open, productId]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    // Validate required fields
    if (!formData.name) {
      toast.error('Product name is required');
      setActiveTab('basic');
      return;
    }
    if (!formData.category_id) {
      toast.error('Category is required');
      setActiveTab('basic');
      return;
    }

    setSaving(true);
    try {
      const url = isEditMode 
        ? `${API_URL}/api/catalog/products/${productId}`
        : `${API_URL}/api/catalog/products`;
      
      const res = await fetch(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await res.json();
      
      if (res.ok) {
        toast.success(isEditMode ? 'Product updated' : 'Product created');
        onSave?.(data.product);
        onOpenChange(false);
      } else {
        toast.error(data.detail || 'Failed to save product');
      }
    } catch (error) {
      toast.error('Error saving product');
      console.error(error);
    }
    setSaving(false);
  };

  // === VARIANT MANAGEMENT (Matrix Pricing) ===
  const addVariant = () => {
    setFormData(prev => ({
      ...prev,
      variants: [...prev.variants, {
        sku_suffix: '',
        name: '',
        dimensions: '',
        size: '',
        color: '',
        cost: 0,
        retail: 0,
        qty_pricing: []
      }]
    }));
  };

  const updateVariant = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.map((v, i) => i === index ? { ...v, [field]: value } : v)
    }));
  };

  const removeVariant = (index) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index)
    }));
  };

  // === MATERIAL OPTIONS (Per Sqft Pricing) ===
  const addMaterialOption = () => {
    setFormData(prev => ({
      ...prev,
      material_options: [...prev.material_options, {
        name: '',
        rate: 0,
        min_charge: 0
      }]
    }));
  };

  const updateMaterialOption = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      material_options: prev.material_options.map((m, i) => 
        i === index ? { ...m, [field]: value } : m
      )
    }));
  };

  const removeMaterialOption = (index) => {
    setFormData(prev => ({
      ...prev,
      material_options: prev.material_options.filter((_, i) => i !== index)
    }));
  };

  // Get category details for current selection
  const selectedCategory = categories.find(c => c.id === formData.category_id);

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="product-form-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Package className="w-5 h-5" />
            {isEditMode ? 'Edit Product' : 'Create New Product'}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="pricing">Pricing</TabsTrigger>
              {formData.pricing_type === 'matrix' && (
                <TabsTrigger value="variants">Variants ({formData.variants.length})</TabsTrigger>
              )}
              {formData.pricing_type === 'per_sqft' && (
                <TabsTrigger value="sqft">Sq Ft Options</TabsTrigger>
              )}
              <TabsTrigger value="images">Images</TabsTrigger>
            </TabsList>

            {/* Basic Info Tab */}
            <TabsContent value="basic" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {/* Left Column */}
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="name">Product Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      placeholder="Enter product name"
                      data-testid="product-name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="sku">SKU (auto-generated if empty)</Label>
                    <Input
                      id="sku"
                      value={formData.sku}
                      onChange={(e) => handleChange('sku', e.target.value)}
                      placeholder="CA-AWD-CRY-0001"
                      className="font-mono"
                      data-testid="product-sku-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      placeholder="Product description..."
                      rows={4}
                      data-testid="product-description-input"
                    />
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="category">Category *</Label>
                    {loadingCategories ? (
                      <div className="flex items-center gap-2 p-2 border rounded">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-sm text-gray-500">Loading...</span>
                      </div>
                    ) : (
                      <Select 
                        value={formData.category_id} 
                        onValueChange={(v) => handleChange('category_id', v)}
                      >
                        <SelectTrigger data-testid="category-select">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map(cat => (
                            <SelectItem key={cat.id} value={cat.id}>
                              <span className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs">{cat.product_type}</Badge>
                                {cat.name}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {selectedCategory && (
                      <p className="text-xs text-gray-500 mt-1">
                        Type: {selectedCategory.product_type} | Default pricing: {selectedCategory.pricing_type}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="pricing_type">Pricing Type</Label>
                    <Select 
                      value={formData.pricing_type} 
                      onValueChange={(v) => handleChange('pricing_type', v)}
                    >
                      <SelectTrigger data-testid="pricing-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PRICING_TYPES.map(pt => (
                          <SelectItem key={pt.value} value={pt.value}>
                            <div>
                              <span className="font-medium">{pt.label}</span>
                              <span className="text-xs text-gray-500 ml-2">{pt.description}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {/* Warning when changing pricing type in edit mode */}
                    {isEditMode && originalPricingType && formData.pricing_type !== originalPricingType && (
                      <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div className="text-xs text-amber-800">
                          <p className="font-medium">Changing pricing type</p>
                          <p>From &quot;{originalPricingType}&quot; to &quot;{formData.pricing_type}&quot;. Existing pricing data may be lost.</p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="lead_time">Lead Time (days)</Label>
                    <Input
                      id="lead_time"
                      type="number"
                      min="1"
                      value={formData.lead_time_days}
                      onChange={(e) => handleChange('lead_time_days', parseInt(e.target.value) || 1)}
                      data-testid="lead-time-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="is_active"
                        checked={formData.is_active}
                        onChange={(e) => handleChange('is_active', e.target.checked)}
                        className="rounded"
                      />
                      <Label htmlFor="is_active">Active</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="taxable"
                        checked={formData.taxable}
                        onChange={(e) => handleChange('taxable', e.target.checked)}
                        className="rounded"
                      />
                      <Label htmlFor="taxable">Taxable</Label>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Pricing Tab */}
            <TabsContent value="pricing" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <DollarSign className="w-4 h-4" /> 
                    {formData.pricing_type === 'fixed' ? 'Fixed Pricing' : 
                     formData.pricing_type === 'matrix' ? 'Matrix Pricing (Configure in Variants tab)' :
                     formData.pricing_type === 'per_sqft' ? 'Per Square Foot Pricing' :
                     'Option Matrix Pricing'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Fixed Pricing */}
                  {formData.pricing_type === 'fixed' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Base/Retail Price *</Label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.retail_price}
                            onChange={(e) => handleChange('retail_price', parseFloat(e.target.value) || 0)}
                            className="pl-7"
                            data-testid="retail-price-input"
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Cost Price (optional)</Label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.cost_price || ''}
                            onChange={(e) => handleChange('cost_price', parseFloat(e.target.value) || 0)}
                            className="pl-7"
                            data-testid="cost-price-input"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Matrix Pricing Info */}
                  {formData.pricing_type === 'matrix' && (
                    <div className="p-4 bg-blue-50 rounded-lg text-sm">
                      <p className="font-medium mb-2">Matrix Pricing</p>
                      <p className="text-gray-600">
                        Configure variants with different sizes/colors and prices in the <strong>Variants</strong> tab.
                        Each variant can have its own cost, retail price, and quantity pricing tiers.
                      </p>
                      <p className="mt-2 text-gray-500">
                        Current variants: {formData.variants.length}
                      </p>
                    </div>
                  )}

                  {/* Per Sqft Pricing */}
                  {formData.pricing_type === 'per_sqft' && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label>Rate per Sq Ft *</Label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={formData.sqft_pricing.rate_per_sqft}
                              onChange={(e) => handleChange('sqft_pricing', {
                                ...formData.sqft_pricing,
                                rate_per_sqft: parseFloat(e.target.value) || 0
                              })}
                              className="pl-7"
                              data-testid="sqft-rate-input"
                            />
                          </div>
                        </div>
                        <div>
                          <Label>Minimum Sq Ft</Label>
                          <Input
                            type="number"
                            step="0.1"
                            min="0.1"
                            value={formData.sqft_pricing.min_sqft}
                            onChange={(e) => handleChange('sqft_pricing', {
                              ...formData.sqft_pricing,
                              min_sqft: parseFloat(e.target.value) || 1
                            })}
                            data-testid="sqft-min-input"
                          />
                        </div>
                        <div>
                          <Label>Minimum Charge</Label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={formData.sqft_pricing.min_charge}
                              onChange={(e) => handleChange('sqft_pricing', {
                                ...formData.sqft_pricing,
                                min_charge: parseFloat(e.target.value) || 0
                              })}
                              className="pl-7"
                              data-testid="sqft-min-charge-input"
                            />
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500">
                        Configure material options with different rates in the <strong>Sq Ft Options</strong> tab.
                      </p>
                    </div>
                  )}

                  {/* Option Matrix Info */}
                  {formData.pricing_type === 'option_matrix' && (
                    <div className="p-4 bg-purple-50 rounded-lg text-sm">
                      <p className="font-medium mb-2">Option Matrix Pricing</p>
                      <p className="text-gray-600">
                        This pricing type allows complex option combinations (e.g., paper stock + quantity + turnaround).
                        Advanced configuration is typically imported from vendor catalogs.
                      </p>
                      <p className="mt-2 text-gray-500">
                        Current price table entries: {formData.price_table.length}
                      </p>
                    </div>
                  )}

                  {/* Pricing Source */}
                  <div className="pt-4 border-t">
                    <Label>Pricing Source</Label>
                    <Select 
                      value={formData.pricing_source} 
                      onValueChange={(v) => handleChange('pricing_source', v)}
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="manual">Manual</SelectItem>
                        <SelectItem value="calculated">Calculated (Cost × Markup)</SelectItem>
                        <SelectItem value="imported">Imported (From Vendor)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Variants Tab (Matrix Pricing) */}
            {formData.pricing_type === 'matrix' && (
              <TabsContent value="variants" className="space-y-4">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-medium">Product Variants</h3>
                    <p className="text-sm text-gray-500">Add size/color variants with their prices</p>
                  </div>
                  <Button onClick={addVariant} size="sm" data-testid="add-variant-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Variant
                  </Button>
                </div>

                {formData.variants.length === 0 ? (
                  <div className="text-center py-12 border-2 border-dashed rounded-lg">
                    <Layers className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-gray-500">No variants yet</p>
                    <Button onClick={addVariant} variant="outline" className="mt-3">
                      <Plus className="w-4 h-4 mr-2" />
                      Add First Variant
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {formData.variants.map((variant, index) => (
                      <Card key={index} className="p-4">
                        <div className="flex justify-between items-start mb-3">
                          <Badge variant="outline">Variant {index + 1}</Badge>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => removeVariant(index)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-4 gap-3">
                          <div>
                            <Label className="text-xs">Name</Label>
                            <Input
                              value={variant.name}
                              onChange={(e) => updateVariant(index, 'name', e.target.value)}
                              placeholder="e.g., Small"
                              className="text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">SKU Suffix</Label>
                            <Input
                              value={variant.sku_suffix}
                              onChange={(e) => updateVariant(index, 'sku_suffix', e.target.value)}
                              placeholder="e.g., SM"
                              className="text-sm font-mono"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Size/Dimensions</Label>
                            <Input
                              value={variant.dimensions || variant.size || ''}
                              onChange={(e) => updateVariant(index, 'dimensions', e.target.value)}
                              placeholder="e.g., 6 inch"
                              className="text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Color</Label>
                            <Input
                              value={variant.color || ''}
                              onChange={(e) => updateVariant(index, 'color', e.target.value)}
                              placeholder="e.g., Gold"
                              className="text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Cost ($)</Label>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={variant.cost || ''}
                              onChange={(e) => updateVariant(index, 'cost', parseFloat(e.target.value) || 0)}
                              className="text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Retail ($)</Label>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={variant.retail || ''}
                              onChange={(e) => updateVariant(index, 'retail', parseFloat(e.target.value) || 0)}
                              className="text-sm"
                            />
                          </div>
                          <div className="col-span-2">
                            <Label className="text-xs">Margin</Label>
                            <div className="h-9 flex items-center text-sm text-gray-600">
                              {variant.cost && variant.retail ? (
                                <span className="text-green-600 font-medium">
                                  {(((variant.retail - variant.cost) / variant.cost) * 100).toFixed(0)}% markup
                                </span>
                              ) : (
                                <span className="text-gray-400">Set cost & retail to see margin</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
            )}

            {/* Sq Ft Options Tab */}
            {formData.pricing_type === 'per_sqft' && (
              <TabsContent value="sqft" className="space-y-4">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-medium">Material Options</h3>
                    <p className="text-sm text-gray-500">Add different material types with their pricing</p>
                  </div>
                  <Button onClick={addMaterialOption} size="sm" data-testid="add-material-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Material
                  </Button>
                </div>

                {formData.material_options.length === 0 ? (
                  <div className="text-center py-12 border-2 border-dashed rounded-lg">
                    <Layers className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-gray-500">No material options yet</p>
                    <p className="text-xs text-gray-400 mt-1">Base rate will apply to all orders</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {formData.material_options.map((material, index) => (
                      <Card key={index} className="p-4">
                        <div className="flex items-center gap-4">
                          <div className="flex-1">
                            <Label className="text-xs">Material Name</Label>
                            <Input
                              value={material.name}
                              onChange={(e) => updateMaterialOption(index, 'name', e.target.value)}
                              placeholder="e.g., Premium Vinyl"
                              className="text-sm"
                            />
                          </div>
                          <div className="w-32">
                            <Label className="text-xs">Rate/Sq Ft</Label>
                            <div className="relative">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-500">$</span>
                              <Input
                                type="number"
                                step="0.01"
                                value={material.rate || ''}
                                onChange={(e) => updateMaterialOption(index, 'rate', parseFloat(e.target.value) || 0)}
                                className="pl-5 text-sm"
                              />
                            </div>
                          </div>
                          <div className="w-32">
                            <Label className="text-xs">Min Charge</Label>
                            <div className="relative">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-500">$</span>
                              <Input
                                type="number"
                                step="0.01"
                                value={material.min_charge || ''}
                                onChange={(e) => updateMaterialOption(index, 'min_charge', parseFloat(e.target.value) || 0)}
                                className="pl-5 text-sm"
                              />
                            </div>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => removeMaterialOption(index)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 mt-5"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
            )}

            {/* Images Tab */}
            <TabsContent value="images" className="space-y-4">
              {/* Image Upload */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                {uploadingImage ? (
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                    <span className="text-gray-600">Uploading...</span>
                  </div>
                ) : (
                  <>
                    <Upload className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                    <p className="text-gray-600 mb-2">
                      {isEditMode ? 'Drop an image here or click to upload' : 'Save product first to upload images'}
                    </p>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="image-upload"
                      disabled={!isEditMode}
                    />
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => document.getElementById('image-upload').click()}
                      disabled={!isEditMode}
                      data-testid="upload-image-btn"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Image
                    </Button>
                    <p className="text-xs text-gray-400 mt-2">JPG, PNG, WebP, GIF (max 5MB)</p>
                  </>
                )}
              </div>

              {/* Or enter URL manually */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-gray-500">Or enter URL</span>
                </div>
              </div>

              <div>
                <Label htmlFor="image_url">Image URL</Label>
                <Input
                  id="image_url"
                  value={formData.image_url}
                  onChange={(e) => handleChange('image_url', e.target.value)}
                  placeholder="https://example.com/product-image.jpg"
                  data-testid="image-url-input"
                />
              </div>

              {formData.image_url && (
                <div className="border rounded-lg p-4">
                  <Label className="text-xs text-gray-500 mb-2 block">Preview</Label>
                  <img 
                    src={formData.image_url.startsWith('/api') ? `${API_URL}${formData.image_url}` : formData.image_url} 
                    alt="Product preview"
                    className="max-w-xs max-h-48 object-contain rounded"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="mt-2 text-red-600"
                    onClick={() => handleChange('image_url', '')}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Remove
                  </Button>
                </div>
              )}
            </TabsContent>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t mt-6">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-product-btn">
                {saving ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                ) : (
                  <><Save className="w-4 h-4 mr-2" /> {isEditMode ? 'Update Product' : 'Create Product'}</>
                )}
              </Button>
            </div>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProductFormModal;
